import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  TEMPLATE_LAYOUT,
  TEMPLATE_LAYOUT_OPTIONS,
} from "../../constants/dashboard";
import { generateProjectId } from "../../constants/general";
import { setHeight, setLayout, setWidth } from "../../store/editor/canvasSlice";
import { setActiveLayout } from "../../store/templates/templatesSlice";
import {
  loadHorizontalTemplates,
  loadVerticalTemplates,
} from "../../thunks/dashboardThunks";
import CreateRail from "../common/CreateRail";
import Rail from "../common/Rail";
import AvatarSlider from "./AvatarSlider";
import Banner from "./Banner";
import TopBar from "./TopBar";
import { getFromLocal } from "../../utils/localStorage";

const ContentArea = ({ myProject, isDashboard }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    userTemplates,
    totalUserTemplatesCount,
    userTemplateLoading,
    hasMoreUserTemplate,
    activeLayout,
    horizontalTemplates,
    verticalTemplates,
    hasMoreVerticalTemplates,
    hasMoreHorizontalTemplates,
  } = useSelector((state) => state.templates);

  const userData = useSelector((state) => state?.user?.details);
  const {
    trial,
    isAdmin,
    aiAvatar,
    aiCaptions,
    aiCustomAvatar,
    aiDebugging,
    aiVoices,
    docToVideo,
    faceLessVideo,
    linkToVideo,
    nonVoiceVideo,
    isLTD,
  } = useMemo(() => {
    const isAdmin = userData?.data?.admin;
    const isLTD = userData?.data?.user_type === "LTD";
    const trial = userData?.data?.trial;
    const aiAvatar = userData?.data?.ai_avatar;
    const aiCaptions = userData?.data?.ai_captions;
    const aiDebugging = userData?.data?.ai_dubbing;
    const linkToVideo = userData?.data?.link_to_video;
    const docToVideo = userData?.data?.doc_to_video;
    const aiVoices = userData?.data?.ai_voices;
    const nonVoiceVideo = userData?.data?.non_voice_video;
    const faceLessVideo = userData?.data?.faceless_video;
    const aiCustomAvatar = userData?.data?.ai_custom_avatar;
    return {
      trial,
      isAdmin,
      aiAvatar,
      aiCaptions,
      aiDebugging,
      linkToVideo,
      docToVideo,
      aiVoices,
      nonVoiceVideo,
      faceLessVideo,
      aiCustomAvatar,
      isLTD,
    };
  }, [userData]);
  // const trial = useSelector((state) => state?.user?.details?.data?.trial);

  const handleTabChange = (value) => dispatch(setActiveLayout(value));

  const isHorizontalLayout = useMemo(
    () => activeLayout === TEMPLATE_LAYOUT.HORIZONTAL,
    [activeLayout]
  );
  const isVerticalLayout = useMemo(
    () => activeLayout === TEMPLATE_LAYOUT.VERTICAL,
    [activeLayout]
  );

  useEffect(() => {
    fetchLayoutTemplate(
      0,
      !myProject ? (isVerticalLayout ? 10 : 8) : undefined
    );
  }, [isHorizontalLayout, isVerticalLayout]);

  const videoLength = getFromLocal("videos");

  const createItems = useMemo(
    () => [
      {
        iconUrl: "/tool/dashboard/static/images/create.png",
        label: "Create Video with Avatar",
        type: "user-template",
        new: true,
        disabled: !isLTD && !isAdmin && videoLength === 0,
        tooltip:
          !isLTD &&
          !isAdmin &&
          videoLength === 0 &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create-video-from-avatar.png",
        label: "Create only Avatar Video",
        disabled: !isLTD && !isAdmin && !aiAvatar,
        type: "only-avatar",
        isOnlyAvatar: true,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !aiAvatar &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/user-template.svg",
        label: "Create a featured template",
        type: "template",
        new: true,
        isHidden: !isAdmin,
      },
      {
        iconUrl: "/tool/dashboard/static/images/faceless-video.svg",
        label: "Create Faceless Video",
        isFaceless: true,
        disabled:
          !isLTD && !isAdmin && (trial ? videoLength === 0 : !faceLessVideo),
        tooltip:
          !isLTD &&
          !isAdmin &&
          (trial ? videoLength === 0 : !faceLessVideo) &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create-video-from-template.png",
        label: "Create Video from template",
        navigate: "/tool/templates",
        disabled: !isLTD && !isAdmin && videoLength === 0,
        tooltip:
          !isLTD &&
          !isAdmin &&
          videoLength === 0 &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create-video-from-document.png",
        label: "Create Video from Document",
        isDoc: true,
        disabled: !isLTD && !isAdmin && !docToVideo,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !docToVideo &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/subtitle.svg",
        label: "Generate AI Subtitles",
        navigate: `/tool/subtitles/${generateProjectId()}`,
        isSubtitle: true,
        disabled: !isLTD && !isAdmin && !aiCaptions,
        tooltip:
          !isAdmin &&
          !isLTD &&
          !aiCaptions &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/link.svg",
        label: "Create Video from link",
        isLinkVideo: true,
        disabled: !isLTD && !isAdmin && !linkToVideo,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !linkToVideo &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create.png",
        label: "AI Dubbing",
        isAIdubbingVoice: true,
        navigate: `/tool/ai-dubbing/${generateProjectId()}`,
        disabled: !isLTD && !isAdmin && !aiDebugging,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !aiDebugging &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create-video-from-avatar.png",
        label: "Just AI Voices",
        type: "ai-voices",
        isOnlyAIVoice: true,
        disabled: !isLTD && !isAdmin && !aiVoices,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !aiVoices &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
      {
        iconUrl: "/tool/dashboard/static/images/create-video-from-avatar.png",
        label: "Non Voice Videos",
        disabled: !isLTD && !isAdmin && !nonVoiceVideo,
        isNonVoice: true,
        tooltip:
          !isLTD &&
          !isAdmin &&
          !nonVoiceVideo &&
          "Upgrade now to unlock the full potential of vidBoard.ai!",
      },
    ],
    [
      trial,
      isAdmin,
      aiAvatar,
      aiCaptions,
      aiDebugging,
      linkToVideo,
      docToVideo,
      aiVoices,
      nonVoiceVideo,
      faceLessVideo,
      aiCustomAvatar,
      isLTD,
    ]
  );

  const onRecentTemplateClick = (item) => {
    const { id, layout, layoutDimension } = item || {};

    const newDimension =
      layoutDimension !== null ? layoutDimension : "w=1920xh=1080";
    const [widthPart, heightPart] = newDimension?.split("x");

    const width = widthPart.split("=")[1];
    const height = heightPart.split("=")[1];
    console.log("width and height", width, height);
    const queryParams = new URLSearchParams();
    queryParams.append("width", width);
    queryParams.append("height", height);
    queryParams.append("layout", layout);
    dispatch(setHeight(height));
    dispatch(setWidth(width));
    dispatch(setLayout(layout));
    const url = "/tool/editor/user-template/" + id;
    // Append query parameters to URL
    const finalUrl = url + "?" + queryParams.toString();
    navigate(finalUrl);
  };

  const fetchLayoutTemplate = (page, offset) => {
    if (isVerticalLayout) {
      dispatch(loadVerticalTemplates(page || 0, offset));
    }
    if (isHorizontalLayout) {
      dispatch(loadHorizontalTemplates(page || 0, offset));
    }
  };
  const fetchMore = (page) => {
    return fetchLayoutTemplate(page);
  };

  const hasMore = useMemo(() => {
    if (isVerticalLayout) {
      return hasMoreVerticalTemplates;
    }
    return hasMoreHorizontalTemplates;
  }, [isVerticalLayout, hasMoreHorizontalTemplates, hasMoreVerticalTemplates]);

  const filteredTemplates = useMemo(() => {
    const templates = isVerticalLayout
      ? verticalTemplates
      : horizontalTemplates;
    if (!myProject) {
      return templates.slice(0, isVerticalLayout ? 8 : 8);
    }
    return templates;
  }, [verticalTemplates, horizontalTemplates, isVerticalLayout]);

  return (
    <div className="w-85  bg-gray-100 p-0 space-y-2 ml-15 min-h-screen">
      <TopBar />
      <Banner imageUrl="/tool/dashboard/static/images/banner.png" />
      {!myProject && (
        <CreateRail items={createItems.filter((item) => !item.isHidden)} />
      )}

      <div className="mx-4 ">
        <Rail
          key={isVerticalLayout ? "vertical-layout" : "horizontal-layout"}
          items={filteredTemplates}
          railTitle={"Recent Projects"}
          onClicked={onRecentTemplateClick}
          totalCount={totalUserTemplatesCount}
          fetchMore={fetchMore}
          loading={userTemplateLoading}
          isUserTemplate
          isSlider={false}
          hasMore={hasMore}
          tabs={TEMPLATE_LAYOUT_OPTIONS}
          handleTabChange={handleTabChange}
          myProject={myProject}
          isDashboard={isDashboard}
        />
      </div>
      {!myProject && (
        <div className="p-4">
          <AvatarSlider />
        </div>
      )}
      <div className=" border-2 flex px-6 justify-between  bg-[#2C2B66] text-white">
        <div className=" flex  h-auto  items-center">
          <h1 className="text-3xl font-bold mr-4">Stay connected</h1>
          <p className="text-xs">
            Follow us on social media and stay up to date with <br></br>offers,
            promotions, and exciting content.{" "}
          </p>
        </div>
        <div className="py-6 flex ">
          <a
            className="mx-2"
            href="https://m.facebook.com/groups/1046335893007605/?ref=share&mibextid=KtfwRi"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/tool/dashboard/static/images/facebook.png" />
          </a>
          <a
            className="mx-2"
            href="https://x.com/VidboardA?t=7lWEXvgxfbQgVGNA4MFiYw&s=09"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/tool/dashboard/static/images/twitter.png" />
          </a>
          <a
            className="mx-2"
            href="https://in.linkedin.com/company/vidboard-ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/tool/dashboard/static/images/linkedin.png" />
          </a>
          <a
            className="mx-2"
            href="https://www.instagram.com/vidboard.ai/?igsh=MTlmYXAyazB5OTRmYw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src="/tool/dashboard/static/images/Instagram.png" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ContentArea;
